/**
 * Data access for clients, quotes and contracts (supabase/migrations/20261018002000_quotes_contracts.sql). Every
 * read and write goes through RLS; rules (numbers, totals, checks, snapshots) come from `@teslyn/domain`.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  emptyCharges,
  emptySampling,
  emptyTerms,
  normaliseQcRules,
  type ClauseTemplate,
  type ClientContact,
  type ClientOrgDetails,
  type CommercialTerms,
  type Contract,
  type ContractSnapshot,
  type ContractVariation,
  type DeliverableSpec,
  type QcRule,
  type Quote,
  type QuoteAggregate,
  type QuoteArea,
  type QuoteCharge,
  type QuoteOption,
  type QuoteRevision,
  type SamplingSpec,
  type TenantPaymentDetails,
  type TenantProfile,
} from '@teslyn/domain';

interface DbError {
  readonly message: string;
  readonly code?: string;
  readonly hint?: string;
}

/** Database errors in plain words. */
export function plainDbError(e: DbError): string {
  if (e.code === '42501' || /row-level security|permission denied/i.test(e.message)) {
    return 'You do not have permission to make this change.';
  }
  if (e.code === '23505') return 'That number is already used. Choose another.';
  return e.message;
}

function check<T>(r: { data: T | null; error: DbError | null }): T {
  if (r.error) throw new Error(plainDbError(r.error));
  return r.data as T;
}

/** An update or delete that RLS filtered out returns no rows: report it. */
function changed(r: { data: unknown[] | null; error: DbError | null }): void {
  if (r.error) throw new Error(plainDbError(r.error));
  if (!r.data || r.data.length === 0) throw new Error('Nothing was changed. You may not have permission.');
}

export const newId = (): string => crypto.randomUUID();

// ---------------------------------------------------------------- clients

export interface Clients {
  readonly orgs: readonly ClientOrgDetails[];
  readonly contacts: readonly ClientContact[];
}

export async function loadClients(sb: SupabaseClient, tenantId: string): Promise<Clients> {
  const [o, c] = await Promise.all([
    sb.from('client_org').select('*').eq('tenant_id', tenantId).is('deleted_at', null).order('name'),
    sb.from('client_contact').select('*').eq('tenant_id', tenantId).is('deleted_at', null).order('name'),
  ]);
  return {
    orgs: check(o) as unknown as ClientOrgDetails[],
    contacts: check(c) as unknown as ClientContact[],
  };
}

const ORG_COLUMNS = [
  'id',
  'tenant_id',
  'name',
  'legal_name',
  'company_no',
  'address_line1',
  'address_line2',
  'suburb',
  'state',
  'postcode',
  'country',
  'email',
  'web',
] as const;

export async function saveClientOrg(
  sb: SupabaseClient,
  org: ClientOrgDetails,
  userId: string,
): Promise<void> {
  const row: Record<string, unknown> = Object.fromEntries(ORG_COLUMNS.map((k) => [k, org[k]]));
  check(
    await sb
      .from('client_org')
      .upsert({ ...row, updated_by: userId })
      .select('id'),
  );
}

export async function saveContact(sb: SupabaseClient, c: ClientContact, userId: string): Promise<void> {
  const { deleted_at: _deleted, ...row } = c;
  void _deleted;
  check(
    await sb
      .from('client_contact')
      .upsert({ ...row, updated_by: userId })
      .select('id'),
  );
}

export async function removeContact(sb: SupabaseClient, id: string, userId: string): Promise<void> {
  changed(
    await sb
      .from('client_contact')
      .update({ deleted_at: new Date().toISOString(), updated_by: userId })
      .eq('id', id)
      .select('id'),
  );
}

// ---------------------------------------------------------------- quotes

export interface QuoteListRow {
  readonly id: string;
  readonly number: string;
  readonly revision: number;
  readonly status: Quote['status'];
  readonly project_name: string;
  readonly quote_date: string;
  readonly client_name: string;
}

export async function listQuotes(sb: SupabaseClient, tenantId: string): Promise<QuoteListRow[]> {
  const r = await sb
    .from('quote')
    .select('id, number, revision, status, project_name, quote_date, client_org:client_org_id (name)')
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .order('number');
  const rows = check(r) as unknown as Array<
    Omit<QuoteListRow, 'client_name'> & { client_org: { name: string } | { name: string }[] | null }
  >;
  return rows.map(({ client_org, ...q }) => ({
    ...q,
    client_name: (Array.isArray(client_org) ? client_org[0]?.name : client_org?.name) ?? '',
  }));
}

/** Every quote number the tenant has used (deleted ones too), for the next number and the duplicate check. */
export async function quoteNumbers(sb: SupabaseClient, tenantId: string): Promise<string[]> {
  const rows = check(await sb.from('quote').select('number').eq('tenant_id', tenantId)) as unknown as {
    number: string;
  }[];
  return rows.map((r) => r.number);
}

/** The editor's copy of a quote: the aggregate plus the id of its QC limits row. */
export interface QuoteDraft {
  readonly agg: QuoteAggregate;
  readonly qcSpecId: string | null;
}

export async function loadQuote(sb: SupabaseClient, quoteId: string): Promise<QuoteDraft> {
  const [q, options, areas, charges, terms, sampling, deliverables, qc] = await Promise.all([
    sb.from('quote').select('*').eq('id', quoteId).maybeSingle(),
    sb.from('quote_option').select('*').eq('quote_id', quoteId).order('sort'),
    sb.from('quote_area').select('*').eq('quote_id', quoteId).order('sort'),
    sb.from('quote_charge').select('*').eq('quote_id', quoteId),
    sb.from('commercial_terms').select('*').eq('quote_id', quoteId).maybeSingle(),
    sb.from('sampling_spec').select('*').eq('quote_id', quoteId).maybeSingle(),
    sb.from('deliverable_spec').select('*').eq('quote_id', quoteId).order('sort'),
    sb.from('qc_spec').select('id, rules').eq('quote_id', quoteId).maybeSingle(),
  ]);
  const quote = check(q) as unknown as Quote | null;
  if (!quote) throw new Error('This quote does not exist or you may not open it.');
  const ids = [quote.principal_contact_id, quote.notices_contact_id].filter((x): x is string => x !== null);
  const [org, contacts] = await Promise.all([
    quote.client_org_id
      ? sb.from('client_org').select('*').eq('id', quote.client_org_id).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    ids.length
      ? sb.from('client_contact').select('*').in('id', ids)
      : Promise.resolve({ data: [], error: null }),
  ]);
  const contactRows = (check(contacts) ?? []) as unknown as ClientContact[];
  const qcRow = check(qc) as unknown as { id: string; rules: QcRule[] } | null;
  const storedCharges = (check(charges) ?? []) as unknown as QuoteCharge[];
  const num = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
  // decision 171: every quote has at least one option (the migration gave older quotes option A)
  let optionRows = ((check(options) ?? []) as unknown as QuoteOption[]).map((o) => ({
    ...o,
    aircraft_cruise_kt: num(o.aircraft_cruise_kt),
  }));
  if (optionRows.length === 0) {
    optionRows = [
      {
        id: newId(),
        quote_id: quoteId,
        sort: 1,
        label: 'A',
        description: '',
        aircraft_type: '',
        aircraft_cruise_kt: null,
        stated_total_line_km: null,
      },
    ];
  }
  const allCharges = optionRows.flatMap((o) =>
    emptyCharges(quoteId, o.id).map(
      (e) => storedCharges.find((c) => c.option_id === o.id && c.kind === e.kind) ?? e,
    ),
  );
  return {
    qcSpecId: qcRow?.id ?? null,
    agg: {
      quote,
      options: optionRows,
      client: (check(org) as unknown as ClientOrgDetails | null) ?? null,
      principal: contactRows.find((c) => c.id === quote.principal_contact_id) ?? null,
      notices: contactRows.find((c) => c.id === quote.notices_contact_id) ?? null,
      areas: ((check(areas) ?? []) as unknown as QuoteArea[]).map((a) => ({
        ...a,
        rate_per_line_km: num(a.rate_per_line_km),
      })),
      charges: allCharges.map((c) => ({ ...c, amount: num(c.amount) })),
      terms: (check(terms) as unknown as CommercialTerms | null) ?? emptyTerms(quoteId),
      sampling: (check(sampling) as unknown as SamplingSpec | null) ?? emptySampling(quoteId),
      deliverables: (check(deliverables) ?? []) as unknown as DeliverableSpec[],
      qc: normaliseQcRules(qcRow?.rules ?? []),
    },
  };
}

/** A new draft with the tenant's defaults (QC limits per decision 123, payment terms from the clause template). */
export async function createQuote(
  sb: SupabaseClient,
  tenantId: string,
  userId: string,
  number: string,
  qcDefaults: readonly QcRule[],
  params: ClauseTemplate['params'],
): Promise<string> {
  const id = newId();
  const optionId = newId();
  check(await sb.from('quote').insert({ id, tenant_id: tenantId, number, updated_by: userId }).select('id'));
  check(
    await sb
      .from('quote_option')
      .insert({ id: optionId, tenant_id: tenantId, quote_id: id, sort: 1, label: 'A', updated_by: userId })
      .select('id'),
  );
  const t = emptyTerms(id, params);
  await Promise.all([
    sb.from('sampling_spec').insert({ ...emptySampling(id), tenant_id: tenantId }),
    sb.from('commercial_terms').insert({ ...t, tenant_id: tenantId }),
    sb.from('quote_charge').insert(emptyCharges(id, optionId).map((c) => ({ ...c, tenant_id: tenantId }))),
    sb.from('qc_spec').insert({ tenant_id: tenantId, quote_id: id, source: 'quote', rules: qcDefaults }),
  ]).then((rs) => rs.forEach((r) => check(r)));
  return id;
}

const QUOTE_EDITABLE = [
  'number',
  'client_org_id',
  'principal_contact_id',
  'notices_contact_id',
  'project_name',
  'location',
  'located',
  'state',
  'country',
  'job_type',
  'quote_date',
  'valid_until',
  'currency',
  'aircraft_type',
  'aircraft_cruise_kt',
  'bases',
  'est_start_qualifier',
  'est_start_month',
  'est_duration_value',
  'est_duration_unit',
  'est_duration_note',
  'est_daily_flight_h',
  'delivery_working_days',
  'governing_law',
  'coords_datum',
  'conduct_items',
  'fuel_by',
  'permits_by',
  'free_grid_offer',
  'print_boundary_coords',
  'signatory_name',
  'signatory_title',
  'notes',
] as const;

/** Save the whole draft: the quote row and every child table (areas and deliverables removed in the editor go). */
export async function saveQuote(
  sb: SupabaseClient,
  draft: QuoteDraft,
  loaded: QuoteDraft,
  userId: string,
): Promise<QuoteDraft> {
  const { agg } = draft;
  const q = agg.quote;
  const tenant_id = q.tenant_id;
  const quoteRow: Record<string, unknown> = Object.fromEntries(QUOTE_EDITABLE.map((k) => [k, q[k]]));
  changed(
    await sb
      .from('quote')
      .update({ ...quoteRow, updated_by: userId })
      .eq('id', q.id)
      .select('id'),
  );
  // options first: areas and charges point to them (decision 171)
  check(
    await sb
      .from('quote_option')
      .upsert(agg.options.map((o) => ({ ...o, tenant_id, updated_by: userId })))
      .select('id'),
  );
  const goneOptions = loaded.agg.options
    .filter((o) => !agg.options.some((p) => p.id === o.id))
    .map((o) => o.id);
  const goneAreas = loaded.agg.areas.filter((a) => !agg.areas.some((b) => b.id === a.id)).map((a) => a.id);
  const goneDeliverables = loaded.agg.deliverables
    .filter((d) => !agg.deliverables.some((e) => e.id === d.id))
    .map((d) => d.id);
  const ops: Array<PromiseLike<{ data: unknown; error: DbError | null }>> = [
    sb
      .from('sampling_spec')
      .upsert({ ...agg.sampling, tenant_id, updated_by: userId })
      .select('quote_id'),
    sb
      .from('commercial_terms')
      .upsert({ ...agg.terms, tenant_id, updated_by: userId })
      .select('quote_id'),
    sb
      .from('quote_charge')
      .upsert(agg.charges.map((c) => ({ ...c, tenant_id, updated_by: userId })))
      .select('kind'),
  ];
  if (goneAreas.length) ops.push(sb.from('quote_area').delete().in('id', goneAreas).select('id'));
  if (agg.areas.length) {
    ops.push(
      sb
        .from('quote_area')
        .upsert(agg.areas.map((a) => ({ ...a, tenant_id, updated_by: userId })))
        .select('id'),
    );
  }
  if (goneDeliverables.length) {
    ops.push(sb.from('deliverable_spec').delete().in('id', goneDeliverables).select('id'));
  }
  if (agg.deliverables.length) {
    ops.push(
      sb
        .from('deliverable_spec')
        .upsert(agg.deliverables.map((d) => ({ ...d, tenant_id, updated_by: userId })))
        .select('id'),
    );
  }
  ops.push(
    draft.qcSpecId
      ? sb.from('qc_spec').update({ rules: agg.qc }).eq('id', draft.qcSpecId).select('id')
      : sb.from('qc_spec').insert({ tenant_id, quote_id: q.id, source: 'quote', rules: agg.qc }).select('id'),
  );
  for (const r of await Promise.all(ops)) check(r);
  // a removed option takes its areas and charges with it
  if (goneOptions.length) check(await sb.from('quote_option').delete().in('id', goneOptions).select('id'));
  return loadQuote(sb, q.id);
}

export async function loadRevisions(sb: SupabaseClient, quoteId: string): Promise<QuoteRevision[]> {
  const r = await sb.from('quote_revision').select('*').eq('quote_id', quoteId).order('revision');
  return check(r) as unknown as QuoteRevision[];
}

/** "Send": freeze the working revision (AC-Q4); later edits are the next revision. */
export async function sendRevision(sb: SupabaseClient, agg: QuoteAggregate, userId: string): Promise<void> {
  const q = agg.quote;
  check(
    await sb
      .from('quote_revision')
      .update({ status: 'superseded' })
      .eq('quote_id', q.id)
      .eq('status', 'sent')
      .select('id'),
  );
  check(
    await sb
      .from('quote_revision')
      .insert({
        tenant_id: q.tenant_id,
        quote_id: q.id,
        revision: q.revision,
        snapshot: agg,
        sent_by: userId,
      })
      .select('id'),
  );
  changed(
    await sb
      .from('quote')
      .update({ status: 'sent', revision: q.revision + 1, updated_by: userId })
      .eq('id', q.id)
      .select('id'),
  );
}

/** The client's answer; an acceptance records the option the client chose (decision 171). */
export async function setRevisionStatus(
  sb: SupabaseClient,
  rev: QuoteRevision,
  status: 'accepted' | 'declined',
  userId: string,
  chosenOptionId: string | null = null,
): Promise<void> {
  changed(
    await sb
      .from('quote_revision')
      .update({ status, chosen_option_id: status === 'accepted' ? chosenOptionId : null })
      .eq('id', rev.id)
      .select('id'),
  );
  changed(await sb.from('quote').update({ status, updated_by: userId }).eq('id', rev.quote_id).select('id'));
}

// ---------------------------------------------------------------- tenant settings and templates

export interface ClauseVersionRow {
  readonly id: string;
  readonly kind: 'quote' | 'contract';
  readonly version: string;
  readonly status: 'draft' | 'active' | 'retired';
  readonly effective_from: string;
  readonly template: ClauseTemplate;
}

export interface TenantDocuments {
  readonly profile: TenantProfile | null;
  /** Null when not set up, or when the viewer may not read them (field crew). */
  readonly payment: TenantPaymentDetails | null;
  readonly clauses: readonly ClauseVersionRow[];
  readonly qcDefaults: ReadonlyArray<{
    readonly id: string;
    readonly aircraft_type: string;
    readonly rules: QcRule[];
  }>;
}

export async function loadTenantDocuments(sb: SupabaseClient, tenantId: string): Promise<TenantDocuments> {
  const [p, pay, c, qc] = await Promise.all([
    sb.from('tenant_profile').select('*').eq('tenant_id', tenantId).maybeSingle(),
    sb.from('tenant_payment_details').select('*').eq('tenant_id', tenantId).maybeSingle(),
    sb.from('clause_template_version').select('*').eq('tenant_id', tenantId).order('effective_from'),
    sb.from('qc_limit_default').select('id, aircraft_type, rules').eq('tenant_id', tenantId),
  ]);
  const profile = check(p) as unknown as TenantProfile | null;
  return {
    profile: profile ? { ...profile, tax_rate_pct: Number(profile.tax_rate_pct) } : null,
    payment: (check(pay) as unknown as TenantPaymentDetails | null) ?? null,
    clauses: (check(c) ?? []) as unknown as ClauseVersionRow[],
    qcDefaults: (check(qc) ?? []) as unknown as TenantDocuments['qcDefaults'],
  };
}

/** The clause template in force for a kind: the newest active version. */
export function activeClauses(docs: TenantDocuments, kind: 'quote' | 'contract'): ClauseVersionRow | null {
  const active = docs.clauses.filter((c) => c.kind === kind && c.status === 'active');
  return active[active.length - 1] ?? null;
}

export async function saveProfile(sb: SupabaseClient, p: TenantProfile, userId: string): Promise<void> {
  check(
    await sb
      .from('tenant_profile')
      .upsert({ ...p, updated_by: userId })
      .select('tenant_id'),
  );
}

export async function savePayment(
  sb: SupabaseClient,
  p: TenantPaymentDetails,
  userId: string,
): Promise<void> {
  check(
    await sb
      .from('tenant_payment_details')
      .upsert({ ...p, updated_by: userId })
      .select('tenant_id'),
  );
}

export async function addClauseVersion(
  sb: SupabaseClient,
  tenantId: string,
  template: ClauseTemplate,
  userId: string,
): Promise<void> {
  check(
    await sb
      .from('clause_template_version')
      .insert({
        tenant_id: tenantId,
        kind: template.kind,
        version: template.version,
        template,
        updated_by: userId,
      })
      .select('id'),
  );
}

export async function saveQcDefaults(
  sb: SupabaseClient,
  tenantId: string,
  aircraftType: string,
  rules: readonly QcRule[],
  userId: string,
): Promise<void> {
  check(
    await sb
      .from('qc_limit_default')
      .upsert(
        { tenant_id: tenantId, aircraft_type: aircraftType, rules, updated_by: userId },
        {
          onConflict: 'tenant_id,aircraft_type',
        },
      )
      .select('id'),
  );
}

// ---------------------------------------------------------------- contracts

export interface ContractListRow {
  readonly id: string;
  readonly number: string;
  readonly status: Contract['status'];
  readonly contract_date: string;
  readonly signed_at: string | null;
  readonly project_id: string | null;
  readonly quote_id: string;
  readonly quote_revision: number;
}

export async function listContracts(sb: SupabaseClient, tenantId: string): Promise<ContractListRow[]> {
  const r = await sb
    .from('contract')
    .select('id, number, status, contract_date, signed_at, project_id, quote_id, quote_revision')
    .eq('tenant_id', tenantId)
    .order('number');
  return check(r) as unknown as ContractListRow[];
}

export async function loadContract(sb: SupabaseClient, id: string): Promise<Contract> {
  const c = check(
    await sb.from('contract').select('*').eq('id', id).maybeSingle(),
  ) as unknown as Contract | null;
  if (!c) throw new Error('This contract does not exist or you may not open it.');
  return c;
}

export async function makeContract(
  sb: SupabaseClient,
  input: {
    readonly tenantId: string;
    readonly quoteId: string;
    readonly revision: number;
    readonly number: string;
    readonly contractDate: string;
    readonly templateVersionId: string;
    readonly snapshot: ContractSnapshot;
    /** The chosen option the snapshot holds (decision 171). */
    readonly optionId: string | null;
    readonly userId: string;
  },
): Promise<string> {
  const id = newId();
  check(
    await sb
      .from('contract')
      .insert({
        id,
        tenant_id: input.tenantId,
        quote_id: input.quoteId,
        quote_revision: input.revision,
        number: input.number,
        contract_date: input.contractDate,
        template_version_id: input.templateVersionId,
        snapshot: input.snapshot,
        option_id: input.optionId,
        updated_by: input.userId,
      })
      .select('id'),
  );
  return id;
}

export async function setContractStatus(
  sb: SupabaseClient,
  id: string,
  status: 'issued',
  userId: string,
): Promise<void> {
  changed(await sb.from('contract').update({ status, updated_by: userId }).eq('id', id).select('id'));
}

function rpcCheck(r: { data: unknown; error: DbError | null }): unknown {
  if (r.error) throw new Error(plainDbError(r.error));
  return r.data;
}

export async function signContract(
  sb: SupabaseClient,
  id: string,
  date: string,
  fileName: string,
): Promise<void> {
  rpcCheck(await sb.rpc('contract_sign', { p_contract: id, p_signed_at: date, p_file_name: fileName }));
}

/** Record a contract variation (decisions 122 and 258): QC limits, line km, or both (`contract_vary`). */
export async function varyContract(
  sb: SupabaseClient,
  input: {
    readonly contractId: string;
    /** null: the QC limits do not change */
    readonly rules: readonly QcRule[] | null;
    readonly summary: string;
    readonly reason: string;
    readonly clientAgreed: boolean;
    readonly changes: readonly string[];
    readonly columns: {
      readonly qc_hand_sample_pct: number | null;
      readonly qc_test_line_pct: number | null;
    } | null;
    /** Signed change of the line km; null when the km do not change. */
    readonly lineKmChange: number | null;
  },
): Promise<void> {
  rpcCheck(
    await sb.rpc('contract_vary', {
      p_contract: input.contractId,
      p_rules: input.rules,
      p_summary: input.summary,
      p_reason: input.reason,
      p_client_agreed: input.clientAgreed,
      p_changes:
        input.lineKmChange === null
          ? { qc: input.changes }
          : { qc: input.changes, line_km_change: input.lineKmChange },
      p_columns: input.columns,
      p_line_km_change: input.lineKmChange,
    }),
  );
}

export async function createProject(sb: SupabaseClient, contractId: string, setup: unknown): Promise<string> {
  return String(
    rpcCheck(await sb.rpc('create_project_from_contract', { p_contract: contractId, p_setup: setup })),
  );
}

export interface QcVersionRow {
  readonly version: number;
  readonly source: string;
  readonly reason: string;
  readonly rules: QcRule[];
  readonly created_at: string;
}

export async function loadContractHistory(
  sb: SupabaseClient,
  contractId: string,
): Promise<{ readonly variations: ContractVariation[]; readonly qc: QcVersionRow[] }> {
  const [v, q] = await Promise.all([
    sb.from('contract_variation').select('*').eq('contract_id', contractId).order('number'),
    sb
      .from('qc_spec')
      .select('version, source, reason, rules, created_at')
      .eq('contract_id', contractId)
      .order('version'),
  ]);
  return {
    variations: (check(v) ?? []) as unknown as ContractVariation[],
    qc: (check(q) ?? []) as unknown as QcVersionRow[],
  };
}

export interface ContractDocumentRow {
  readonly id: string;
  readonly contract_id: string;
  readonly variant: 'full' | 'op';
  readonly revision: number;
  readonly file_name: string;
  readonly bundle: unknown;
  readonly created_at: string;
}

/** OP documents are readable by every staff member; full ones by commercial readers (RLS). */
export async function listContractDocuments(
  sb: SupabaseClient,
  tenantId: string,
  contractId?: string,
): Promise<ContractDocumentRow[]> {
  let q = sb.from('contract_document').select('*').eq('tenant_id', tenantId);
  if (contractId) q = q.eq('contract_id', contractId);
  return check(await q.order('created_at')) as unknown as ContractDocumentRow[];
}

export async function recordDocument(
  sb: SupabaseClient,
  row: Omit<ContractDocumentRow, 'id' | 'created_at'> & {
    readonly tenant_id: string;
    readonly sha256: string;
  },
  userId: string,
): Promise<void> {
  check(
    await sb
      .from('contract_document')
      .insert({ ...row, created_by: userId })
      .select('id'),
  );
}

/** SHA-256 of a PDF, hex (document rows keep it so a copy can be checked later). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) return '';
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
